import type { OrgAdminKeyCreatedBy, OrgAdminKeySummary } from '@nadoutong/contracts';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { localizeApiError } from '../../api/errorMessages.ts';
import {
  useCreateOrgAdminKey,
  useOrgAdminKeysList,
  useRevokeOrgAdminKey,
} from '../../api/useAdminKeys.ts';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { Modal } from '../../components/Modal.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { clipboardErrorKey, useClipboard } from '../../hooks/useClipboard.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, FormField, Input, Table, TBody, Td, THead, Th, Tr } from '../../ui/index.ts';

/**
 * Management-key list for `/admin-api`.
 *
 * Mirrors the Keys page contract: create returns plaintext once, the
 * reveal dialog does not dismiss on a backdrop click, and revoke goes
 * through ConfirmDialog. Revoked rows stay visible — the list endpoint
 * returns them — with status instead of a second revoke button.
 */

const NAME_MAX = 64;
const ACTIVE_KEY_CAP = 20;

function createdByLabel(createdBy: OrgAdminKeyCreatedBy): string {
  if (createdBy.display_name !== null && createdBy.display_name.length > 0) {
    return createdBy.display_name;
  }
  if (createdBy.email !== null && createdBy.email.length > 0) {
    return createdBy.email;
  }
  return createdBy.account_id;
}

function trimmedNameProblem(raw: string): boolean {
  const name = raw.trim();
  return name.length < 1 || name.length > NAME_MAX;
}

export function ManagementKeys(): JSX.Element {
  const t = useT();
  const keysQ = useOrgAdminKeysList();
  const createM = useCreateOrgAdminKey();
  const [createOpen, setCreateOpen] = useState(false);
  const [plaintext, setPlaintext] = useState<{ key: string; name: string } | null>(null);
  const rows = keysQ.data?.data ?? [];
  const activeCount = rows.filter((row) => row.revoked_at === null).length;

  return (
    <div className="flex flex-col gap-4" data-testid="admin-api-keys">
      <p className="m-0 text-body leading-relaxed text-ink-mid">{t('adminApi.keys.explainer')}</p>
      {activeCount >= ACTIVE_KEY_CAP ? (
        <p
          className="m-0 rounded-lg border border-warn-line bg-warn-tint px-4 py-3 text-body text-ink"
          role="status"
          data-testid="admin-api-key-cap"
        >
          {t('error.code.ADMIN_KEY_LIMIT_REACHED')}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          variant="primary"
          data-testid="admin-api-create"
          onClick={() => setCreateOpen(true)}
        >
          {t('adminApi.keys.create')}
        </Button>
      </div>

      {keysQ.isLoading ? (
        <div className="flex flex-col gap-2" data-testid="admin-api-keys-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
          <span className="sr-only">{t('adminApi.keys.loading')}</span>
        </div>
      ) : keysQ.isError ? (
        <p className="m-0 text-body text-bad" role="alert" data-testid="admin-api-keys-error">
          {localizeApiError(keysQ.error, t)}
        </p>
      ) : rows.length === 0 ? (
        <p className="m-0 text-body text-ink-low" data-testid="admin-api-keys-empty">
          {t('adminApi.keys.empty')}
        </p>
      ) : (
        <KeysTable rows={rows} />
      )}

      {createOpen ? (
        <CreateKeyModal
          isPending={createM.isPending}
          onClose={() => {
            if (!createM.isPending) setCreateOpen(false);
          }}
          onSubmit={async (name) => {
            try {
              const result = await createM.mutateAsync({ name });
              setCreateOpen(false);
              setPlaintext({ key: result.plaintext, name: result.key.name });
              return null;
            } catch (err) {
              return localizeApiError(err, t);
            }
          }}
        />
      ) : null}

      {plaintext !== null ? (
        <PlaintextReveal
          plaintext={plaintext.key}
          keyName={plaintext.name}
          onClose={() => setPlaintext(null)}
        />
      ) : null}
    </div>
  );
}

function KeysTable({ rows }: { readonly rows: readonly OrgAdminKeySummary[] }): JSX.Element {
  const t = useT();
  const revokeM = useRevokeOrgAdminKey();
  const [revokeTarget, setRevokeTarget] = useState<OrgAdminKeySummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  async function handleRevoke(): Promise<void> {
    if (revokeTarget === null) return;
    setRevokeError(null);
    try {
      await revokeM.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
    } catch (err) {
      setRevokeError(localizeApiError(err, t));
    }
  }

  return (
    <>
      <Table testId="admin-api-keys-table">
        <THead>
          <Tr>
            <Th>{t('adminApi.keys.column.name')}</Th>
            <Th>{t('adminApi.keys.column.prefix')}</Th>
            <Th>{t('adminApi.keys.column.created_by')}</Th>
            <Th>{t('adminApi.keys.column.created_at')}</Th>
            <Th>{t('adminApi.keys.column.last_used')}</Th>
            <Th>{t('adminApi.keys.column.status')}</Th>
            <Th>{t('adminApi.keys.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const revoked = row.revoked_at !== null;
            return (
              <Tr key={row.id} data-testid={`admin-api-row-${row.id}`}>
                <Td label={t('adminApi.keys.column.name')} heading emphasis>
                  {row.name}
                </Td>
                <Td label={t('adminApi.keys.column.prefix')} mono>
                  {row.key_prefix}
                </Td>
                <Td label={t('adminApi.keys.column.created_by')}>
                  {createdByLabel(row.created_by)}
                </Td>
                <Td label={t('adminApi.keys.column.created_at')}>
                  {formatTimestamp(row.created_at)}
                </Td>
                <Td label={t('adminApi.keys.column.last_used')}>
                  {row.last_used_at !== null
                    ? formatTimestamp(row.last_used_at)
                    : t('adminApi.keys.never_used')}
                </Td>
                <Td label={t('adminApi.keys.column.status')}>
                  <Chip tone={revoked ? 'neutral' : 'ok'} testId={`admin-api-status-${row.id}`}>
                    {revoked ? t('adminApi.keys.status.revoked') : t('adminApi.keys.status.active')}
                  </Chip>
                </Td>
                <Td label={t('adminApi.keys.column.actions')}>
                  {revoked ? null : (
                    <Button
                      variant="danger"
                      size="sm"
                      data-testid={`admin-api-revoke-${row.id}`}
                      onClick={() => {
                        setRevokeError(null);
                        setRevokeTarget(row);
                      }}
                    >
                      {t('adminApi.keys.revoke')}
                    </Button>
                  )}
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
      {revokeTarget !== null ? (
        <ConfirmDialog
          open
          tone="danger"
          testId="admin-api-revoke-modal"
          title={t('adminApi.keys.revoke_modal.title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('adminApi.keys.revoke_modal.body', { name: revokeTarget.name })}
              </p>
              {revokeError !== null ? (
                <p className="m-0 text-caption font-medium text-bad" role="alert">
                  {revokeError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={
            revokeM.isPending
              ? t('adminApi.keys.revoke_modal.submitting')
              : t('adminApi.keys.revoke_modal.confirm')
          }
          cancelLabel={t('adminApi.keys.revoke_modal.cancel')}
          closeLabel={t('adminApi.keys.revoke_modal.close')}
          typeToConfirm={revokeTarget.name}
          typeToConfirmLabel={t('adminApi.keys.revoke_modal.type_label')}
          loading={revokeM.isPending}
          onOpenChange={(open) => {
            if (!open && !revokeM.isPending) setRevokeTarget(null);
          }}
          onConfirm={() => {
            void handleRevoke();
          }}
        />
      ) : null}
    </>
  );
}

function CreateKeyModal({
  isPending,
  onClose,
  onSubmit,
}: {
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (name: string) => Promise<string | null>;
}): JSX.Element {
  const t = useT();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (trimmedNameProblem(name)) {
      setError(t('adminApi.keys.create_modal.name_invalid'));
      return;
    }
    setError(null);
    const message = await onSubmit(name.trim());
    if (message !== null) setError(message);
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="admin-api-create-title"
      testId="admin-api-create-modal"
    >
      <form
        className="flex flex-col gap-5 p-6"
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
      >
        <h2 id="admin-api-create-title" className="m-0 text-title text-ink">
          {t('adminApi.keys.create_modal.title')}
        </h2>
        <p className="m-0 text-body text-ink-mid">{t('adminApi.keys.create_modal.description')}</p>
        <FormField
          label={t('adminApi.keys.create_modal.name')}
          fieldId="admin-api-create-name"
          required
          hint={error === null ? t('adminApi.keys.create_modal.name_hint') : undefined}
          error={error}
        >
          <Input
            id="admin-api-create-name"
            data-testid="admin-api-create-name"
            value={name}
            maxLength={NAME_MAX}
            autoComplete="off"
            disabled={isPending}
            invalid={error !== null}
            aria-describedby={error !== null ? 'admin-api-create-name-error' : undefined}
            onChange={(event) => setName(event.currentTarget.value)}
          />
        </FormField>
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            {t('adminApi.keys.create_modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isPending}
            data-testid="admin-api-create-submit"
          >
            {isPending
              ? t('adminApi.keys.create_modal.submitting')
              : t('adminApi.keys.create_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PlaintextReveal({
  plaintext,
  keyName,
  onClose,
}: {
  readonly plaintext: string;
  readonly keyName: string;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  const [hasCopied, setHasCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    const ok = await copy(plaintext);
    if (ok) setHasCopied(true);
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="admin-api-plaintext-title"
      testId="admin-api-plaintext"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col gap-5 p-6">
        <h2 id="admin-api-plaintext-title" className="m-0 text-title text-ink">
          {t('adminApi.keys.plaintext.title', { name: keyName })}
        </h2>
        <p
          className="m-0 rounded-lg border border-warn-line bg-warn-tint px-4 py-3 text-body font-medium text-ink"
          data-testid="admin-api-plaintext-warning"
          role="alert"
        >
          {t('adminApi.keys.plaintext.warning')}
        </p>
        <button
          type="button"
          onClick={() => void handleCopy()}
          data-testid="admin-api-plaintext-copy"
          aria-label={t('adminApi.keys.plaintext.copy_aria')}
          className="flex flex-col items-start gap-2 rounded-lg bg-well p-4 text-left transition-colors duration-150 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <span
            data-testid="admin-api-plaintext-value"
            className="w-full break-all font-mono text-body text-forest"
          >
            {plaintext}
          </span>
          <span className="text-caption text-ink-low">
            {copied ? t('adminApi.keys.plaintext.copied') : t('adminApi.keys.plaintext.copy')}
          </span>
        </button>
        {error !== null ? (
          <p className="m-0 text-caption font-medium text-bad" role="alert">
            {t(clipboardErrorKey(error))}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose} data-testid="admin-api-plaintext-close">
            {t('adminApi.keys.plaintext.close')}
          </Button>
          <Button
            variant="primary"
            data-testid="admin-api-plaintext-done"
            onClick={() => {
              if (hasCopied) {
                onClose();
                return;
              }
              void handleCopy();
            }}
          >
            {hasCopied ? t('adminApi.keys.plaintext.done') : t('adminApi.keys.plaintext.copy')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
